import React, { useEffect, useRef, useState } from 'react';
import { Container, Input, Row, Col, Button } from 'reactstrap';
import ArtistDetail from './ArtistDetail';
// import 'bootstrap/dist/css/bootstrap.css';

const ARTISTS_FILTERS_STORAGE_KEY = 'artistsPageFilters';
const ARTISTS_SCROLL_STORAGE_KEY = 'artistsPageScrollY';
const ARTISTS_EXPANDED_MEDIA_STORAGE_KEY = 'artistsExpandedMediaArtistGUIDs';
const ARTIST_SORT_OPTIONS = {
    NAME_ASC: 'artistNameAsc',
    RANKING_DESC: 'rankingDesc',
    FOLLOWERS_DESC: 'followersDesc'
};

const getAssociatedFollowers = (artist) => {
    return (artist?.soundcloudUser || []).reduce((maxFollowers, user) => {
        const followers = Number(
            user?.mediaMetaData?.followers ??
            user?.mediaMetaData?.followers_count ??
            0
        );
        if (!Number.isFinite(followers)) return maxFollowers;
        return Math.max(maxFollowers, followers);
    }, 0);
};

const readPersistedFilters = () => {
    try {
        const raw = localStorage.getItem(ARTISTS_FILTERS_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        return {
            selectedEvent: typeof parsed?.selectedEvent === 'string' ? parsed.selectedEvent : '',
            selectedEventSchedule: typeof parsed?.selectedEventSchedule === 'string' ? parsed.selectedEventSchedule : '',
            selectedStage: typeof parsed?.selectedStage === 'string' ? parsed.selectedStage : '',
            selectedSort: Object.values(ARTIST_SORT_OPTIONS).includes(parsed?.selectedSort)
                ? parsed.selectedSort
                : ARTIST_SORT_OPTIONS.NAME_ASC,
            showLongSetMedia: parsed?.showLongSetMedia !== false,
            showSongMedia: parsed?.showSongMedia !== false,
            showUnrankedOnly: parsed?.showUnrankedOnly === true
        };
    } catch (_error) {
        return null;
    }
};

const readPersistedExpandedMedia = () => {
    try {
        const raw = localStorage.getItem(ARTISTS_EXPANDED_MEDIA_STORAGE_KEY);
        if (!raw) return [];

        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((value) => typeof value === 'string' && value.length > 0)
            : [];
    } catch (_error) {
        return [];
    }
};

const Artists = () => {
    const persistedFilters = readPersistedFilters();
    const persistedExpandedMedia = readPersistedExpandedMedia();
    const hasRestoredScrollRef = useRef(false);
    const artistsRequestControllerRef = useRef(null);
    const artistsRequestIdRef = useRef(0);

    const [userGUID, ] = useState(localStorage.getItem('userGUID') ? localStorage.getItem('userGUID') : process.env.REACT_APP_USER_GUID || '');

    const [data, setData] = useState([]);
    const [events, setEvents] = useState([]);
    const [eventSchedules, setEventSchedules] = useState([]);
    const [selectedEventSchedule, setSelectedEventSchedule] = useState(persistedFilters?.selectedEventSchedule || '');
    const [stages, setStages] = useState([]);
    const [selectedStage, setSelectedStage] = useState(persistedFilters?.selectedStage || '');
    const [selectedEvent, setSelectedEvent] = useState(persistedFilters?.selectedEvent || '');
    const [selectedSort, setSelectedSort] = useState(persistedFilters?.selectedSort || ARTIST_SORT_OPTIONS.NAME_ASC);
    const [selectedArtists, setSelectedArtists] = useState([]);
    const [showLongSetMedia, setShowLongSetMedia] = useState(persistedFilters?.showLongSetMedia ?? true);
    const [showSongMedia, setShowSongMedia] = useState(persistedFilters?.showSongMedia ?? true);
    const [showUnrankedOnly, setShowUnrankedOnly] = useState(persistedFilters?.showUnrankedOnly ?? false);
    const [expandedMediaArtistGUIDs, setExpandedMediaArtistGUIDs] = useState(persistedExpandedMedia);
    const [autoAssociateLoading, setAutoAssociateLoading] = useState(false);
    const [autoAssociateSummary, setAutoAssociateSummary] = useState(null);

    const filteredEventSchedules = React.useMemo(() => {
        if (!selectedEvent) return eventSchedules;
        return eventSchedules.filter(
            (eventSchedule) => eventSchedule.eventScheduleEventGUID === selectedEvent
        );
    }, [eventSchedules, selectedEvent]);

    const filteredStages = React.useMemo(() => {
        if (!selectedEvent) return stages;
        return stages.filter((stage) => stage.stageEventGUID === selectedEvent);
    }, [stages, selectedEvent]);

    const getArtists = () => {
        if (artistsRequestControllerRef.current) {
            artistsRequestControllerRef.current.abort();
        }

        const controller = new AbortController();
        const requestId = artistsRequestIdRef.current + 1;
        artistsRequestIdRef.current = requestId;
        artistsRequestControllerRef.current = controller;

        fetch('/api/artist/filter', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
                userGUID,
                eventGUID: selectedEvent,
                eventScheduleGUID: selectedEventSchedule,
                stageGUID: selectedStage
            })
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((html) => {
                if (html && requestId === artistsRequestIdRef.current) {
                    setData(html); // Only set data if HTTP status is 200-299
                }
            })
            .catch((error) => {
                if (error?.name === 'AbortError') return;
            })
            .finally(() => {
                if (artistsRequestControllerRef.current === controller) {
                    artistsRequestControllerRef.current = null;
                }
            });
    }

    const getEvents = () => {
        fetch(`/api/event/`, {
            method: 'GET',
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((json) => {
                if (json) {
                    setEvents(json); // Only set data if HTTP status is 200-299
                }
            })
            .catch((error) => {
            });
    };

    const getEventSchedules = () => {
        fetch(`/api/event-schedule/`, {
            method: 'GET',
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((json) => {
                if (json) {
                    setEventSchedules(json); // Only set data if HTTP status is 200-299
                }
            })
            .catch((error) => {
            });
    };

    const getStages = () => {
        fetch(`/api/stage/`, {
            method: 'GET',
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((json) => {
                if (json) {
                    setStages(json); // Only set data if HTTP status is 200-299
                }
            })
            .catch((error) => {
            });
    };

    const onChangeSelectedArtists = (e) => {
        const uuid = e.target.name;
        setSelectedArtists((prevSelected) => {
            if (prevSelected.includes(uuid)) {
                // Remove it
                return prevSelected.filter(id => id !== uuid);
            } else {
                // Add it
                return [...prevSelected, uuid];
            }
        });
    }

    const onToggleArtistMedia = (artistGUID) => {
        setExpandedMediaArtistGUIDs((prev) => {
            if (prev.includes(artistGUID)) {
                return prev.filter((guid) => guid !== artistGUID);
            }
            return [...prev, artistGUID];
        });
    };

    const submit = () => {
        fetch(`/api/set`, {
            method: 'POST',
            body: JSON.stringify({ selectedArtists, selectedEvent, selectedEventSchedule, selectedStage }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    getArtists();
                    setSelectedArtists([]);
                }
            })
            .catch((error) => {
            });
    }

    const autoAssociateSoundcloudUsers = () => {
        if (autoAssociateLoading) return;

        setAutoAssociateLoading(true);
        setAutoAssociateSummary(null);

        fetch('/api/soundcloud/artist/auto-associate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                dryRun: false,
                minFollowers: 50000,
                minTracks: 5,
                limit: 250
            })
        })
            .then(async (response) => {
                const json = await response.json().catch(() => null);
                if (!response.ok) {
                    throw new Error(json?.error || 'Auto-associate request failed');
                }
                return json;
            })
            .then((json) => {
                setAutoAssociateSummary(json);
                getArtists();
            })
            .catch((error) => {
                setAutoAssociateSummary({ error: error.message || 'Auto-associate failed' });
            })
            .finally(() => {
                setAutoAssociateLoading(false);
            });
    };

    // const filter = () => {
    //     const matches = data.filter(artist => {
    //         if (!artist.sets || artist.sets.length === 0) return false;

    //         return artist.sets.some(set => {
    //             const matchesEvent = selectedEvent === '' || set.setEventGUID === selectedEventSchedule;
    //             const matchesEventSchedule = selectedEventSchedule === '' || set.setEventScheduleGUID === selectedEventSchedule;
    //             const matchesStage = selectedStage === '' || set.setStageGUID === selectedStage;

    //             // Strict AND: must match both if both are selected
    //             return matchesEvent && matchesEventSchedule && matchesStage;
    //         });
    //     });
    //     setFilteredData(matches);
    // }

    const clearFilter = () => {
        setSelectedEvent('');
        setSelectedEventSchedule('');
        setSelectedStage('');
    };

    const canSubmit = selectedArtists.length > 0;

    const filtered = React.useMemo(() => {
        return data
            .filter((artist) => !showUnrankedOnly || artist.userWeightedScore == null)
            .sort((a, b) => {
                if (selectedSort === ARTIST_SORT_OPTIONS.RANKING_DESC) {
                    const aRank = Number(a.userWeightedScore ?? -1);
                    const bRank = Number(b.userWeightedScore ?? -1);
                    if (bRank !== aRank) return bRank - aRank;
                }

                if (selectedSort === ARTIST_SORT_OPTIONS.FOLLOWERS_DESC) {
                    const aFollowers = getAssociatedFollowers(a);
                    const bFollowers = getAssociatedFollowers(b);
                    if (bFollowers !== aFollowers) return bFollowers - aFollowers;
                }

                return String(a.artistName || '').localeCompare(String(b.artistName || ''), undefined, {
                    sensitivity: 'base'
                });
            });
    }, [data, selectedSort, showUnrankedOnly]);

    useEffect(() => {
        document.documentElement.classList.add('artists-scroll-page');
        document.body.classList.add('artists-scroll-page');

        getEvents();
        getEventSchedules();
        getStages();

        return () => {
            if (artistsRequestControllerRef.current) {
                artistsRequestControllerRef.current.abort();
            }
            document.documentElement.classList.remove('artists-scroll-page');
            document.body.classList.remove('artists-scroll-page');
        };
    }, []);

    useEffect(() => {
        getArtists();
    }, [selectedEvent, selectedEventSchedule, selectedStage]);

    useEffect(() => {
        localStorage.setItem(ARTISTS_FILTERS_STORAGE_KEY, JSON.stringify({
            selectedEvent,
            selectedEventSchedule,
            selectedStage,
            selectedSort,
            showLongSetMedia,
            showSongMedia,
            showUnrankedOnly
        }));
    }, [selectedEvent, selectedEventSchedule, selectedStage, selectedSort, showLongSetMedia, showSongMedia, showUnrankedOnly]);

    useEffect(() => {
        localStorage.setItem(
            ARTISTS_EXPANDED_MEDIA_STORAGE_KEY,
            JSON.stringify(expandedMediaArtistGUIDs)
        );
    }, [expandedMediaArtistGUIDs]);

    useEffect(() => {
        const persistScrollPosition = () => {
            localStorage.setItem(ARTISTS_SCROLL_STORAGE_KEY, String(window.scrollY || 0));
        };

        window.addEventListener('scroll', persistScrollPosition, { passive: true });
        window.addEventListener('pagehide', persistScrollPosition);

        return () => {
            persistScrollPosition();
            window.removeEventListener('scroll', persistScrollPosition);
            window.removeEventListener('pagehide', persistScrollPosition);
        };
    }, []);

    useEffect(() => {
        if (hasRestoredScrollRef.current) return;
        if (!data.length) return;

        const savedScrollY = Number(localStorage.getItem(ARTISTS_SCROLL_STORAGE_KEY) || 0);
        if (!Number.isFinite(savedScrollY) || savedScrollY <= 0) {
            hasRestoredScrollRef.current = true;
            return;
        }

        hasRestoredScrollRef.current = true;
        window.requestAnimationFrame(() => {
            window.scrollTo(0, savedScrollY);
        });
    }, [data]);

    // useEffect(() => {
    //     // Only reapply filter if one is currently active
    //     if (selectedEvent || selectedEventSchedule || selectedStage) {
    //         filter();
    //     }
    // }, [data]); // re-run filter whenever data is updated

    return (
        <Container className='text-white'>
            <Row className='mt-2'>
                <Col md={4}>
                    Media Types
                </Col>
                <Col className='d-flex align-items-center'>
                    <div className='me-4 d-flex align-items-center'>
                        <Input
                            id='showLongSets'
                            type='checkbox'
                            checked={showLongSetMedia}
                            onChange={(e) => setShowLongSetMedia(e.target.checked)}
                            className='me-2'
                        />
                        <label htmlFor='showLongSets' className='mb-0'>Long Sets</label>
                    </div>
                    <div className='d-flex align-items-center'>
                        <Input
                            id='showSongs'
                            type='checkbox'
                            checked={showSongMedia}
                            onChange={(e) => setShowSongMedia(e.target.checked)}
                            className='me-2'
                        />
                        <label htmlFor='showSongs' className='mb-0'>Regular Songs</label>
                    </div>
                    <div className='ms-4 d-flex align-items-center'>
                        <Input
                            id='showUnrankedOnly'
                            type='checkbox'
                            checked={showUnrankedOnly}
                            onChange={(e) => setShowUnrankedOnly(e.target.checked)}
                            className='me-2'
                        />
                        <label htmlFor='showUnrankedOnly' className='mb-0'>Unranked Only</label>
                    </div>
                </Col>
            </Row>
            <Row className='mt-2'>
                <Col md={4}>
                    Event
                </Col>
                <Col>
                    <Input
                        type="select"
                        name="event"
                        value={selectedEvent}
                        onChange={(e) => {
                            setSelectedEvent(e.target.value);
                            setSelectedEventSchedule('');
                            setSelectedStage('');
                        }}
                    >
                        <option value="">SELECT</option>
                        {events.map(events => (
                            <option
                                key={events.eventGUID}
                                value={events.eventGUID}
                            >{events.eventName}</option>
                        ))}
                    </Input>
                </Col>
            </Row>
            <Row className='mt-2'>
                <Col md={4}>
                    Schedule
                </Col>
                <Col>
                    <Input
                        type="select"
                        name="eventSchedule"
                        value={selectedEventSchedule}
                        onChange={(e) => setSelectedEventSchedule(e.target.value)}
                    >
                        <option value="">SELECT</option>
                        {filteredEventSchedules.map(eventSchedule => (
                            <option
                                key={eventSchedule.eventScheduleGUID}
                                value={eventSchedule.eventScheduleGUID}
                            >{eventSchedule.eventScheduleTitle}</option>
                        ))}
                    </Input>
                </Col>
            </Row>
            <Row className='mt-2'>
                <Col md={4}>
                    Stage
                </Col>
                <Col>
                    <Input
                        type="select"
                        name="stage"
                        value={selectedStage}
                        onChange={(e) => setSelectedStage(e.target.value)}
                    >
                        <option value="">SELECT</option>
                        {filteredStages.map(stage => (
                            <option
                                key={stage.stageGUID}
                                value={stage.stageGUID}
                            >{stage.stageName}</option>
                        ))}
                    </Input>
                </Col>
            </Row>
            <Row className='mt-2'>
                <Col md={4}>
                    Order By
                </Col>
                <Col>
                    <Input
                        type="select"
                        name="artistSort"
                        value={selectedSort}
                        onChange={(e) => setSelectedSort(e.target.value)}
                    >
                        <option value={ARTIST_SORT_OPTIONS.NAME_ASC}>Artist Name (ASC)</option>
                        <option value={ARTIST_SORT_OPTIONS.RANKING_DESC}>Ranking (DESC)</option>
                        <option value={ARTIST_SORT_OPTIONS.FOLLOWERS_DESC}>Followers (DESC)</option>
                    </Input>
                </Col>
            </Row>
            <Row className='mt-2'>
                <Col>
                    <Button
                        color="primary"
                        className='w-100'
                        disabled={!canSubmit}
                        onClick={submit}
                    >Add Selected</Button>
                </Col>
                <Col>
                    <Button
                        className='w-100 text-light'
                        color="secondary"
                        onClick={clearFilter}
                    >Clear</Button>
                </Col>
                <Col>
                    <Button
                        className='w-100 text-light'
                        color="secondary"
                        disabled={autoAssociateLoading}
                        onClick={autoAssociateSoundcloudUsers}
                    >{autoAssociateLoading ? 'Matching...' : 'Auto SC'}</Button>
                </Col>
            </Row>
            {autoAssociateSummary && (
                <Row className='mt-2'>
                    <Col className='text-secondary' style={{ fontSize: 12 }}>
                        {autoAssociateSummary.error
                            ? `Auto SC error: ${autoAssociateSummary.error}`
                            : `Auto SC scanned ${autoAssociateSummary.scannedArtists}, matched ${autoAssociateSummary.matched}, inserted ${autoAssociateSummary.inserted}, skipped ${autoAssociateSummary.skipped}.`}
                    </Col>
                </Row>
            )}
            {filtered.map(artist => (
                <ArtistDetail
                    artistName={artist.artistName}
                    artistGUID={artist.artistGUID}
                    userWeightedScore={artist.userWeightedScore}
                    sets={artist.sets}
                    stages={stages}
                    eventSchedules={eventSchedules}
                    soundcloudMedia={artist.soundcloudMedia}
                    soundcloudUser={artist.soundcloudUser}
                    key={artist.artistGUID}
                    getArtists={getArtists}
                    onChangeSelectedArtists={onChangeSelectedArtists}
                    selectedArtists={selectedArtists}
                    showLongSetMedia={showLongSetMedia}
                    showSongMedia={showSongMedia}
                    showTracks={expandedMediaArtistGUIDs.includes(artist.artistGUID)}
                    onToggleShowTracks={onToggleArtistMedia}
                />
                // <Row key={artist.artistGUID}>
                //     <Col>{artist.artistName}</Col>
                // </Row>
            ))}
        </Container>
    );
}

export default Artists;
